import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';

export interface ColorRangeSelectorRef {
    getColorList: () => string[];
}

interface ColorRangeSelectorProps {
    onChange?: (colors: { start: string; middle?: string; end: string }) => void;
}

const ColorRangeSelector = forwardRef<ColorRangeSelectorRef, ColorRangeSelectorProps>(
  ({ onChange }, ref) => {
    const [startColor, setStartColor] = useState('#0000ff');   // Low
    const [endColor, setEndColor] = useState('#ff0000');       // High
    const [middleColor, setMiddleColor] = useState('#00ff00'); // Middle
    const [useMiddleColor, setUseMiddleColor] = useState(false);

    useImperativeHandle(ref, () => ({
      getColorList: () =>
        useMiddleColor
          ? [startColor, middleColor, endColor]
          : [startColor, endColor],
    }));

    useEffect(() => {
      if (onChange) {
        onChange(
          useMiddleColor
            ? { start: startColor, middle: middleColor, end: endColor }
            : { start: startColor, end: endColor },
        );
      }
    }, [startColor, middleColor, endColor, useMiddleColor, onChange]);

    return (
      <div className='w-full max-w-xs p-3 bg-white rounded-md shadow-sm space-y-3 font-sans'>
        <div className='flex items-center space-x-4'>
          { /* Vertical gradient bar on left */ }
          <div
              className='h-48 w-6 rounded shadow-inner'
              style={{
                backgroundImage: useMiddleColor
                  ? `linear-gradient(to top, ${startColor}, ${middleColor}, ${endColor})`
                  : `linear-gradient(to top, ${startColor}, ${endColor})`,
              }}
              aria-label='Color gradient preview'
              role='img'
          />

          { /* Color pickers aligned vertically to match gradient bar height */ }
          <div className={'flex flex-col justify-between h-48'}>
            { /* High (top) */ }
            <label className='flex flex-col items-center cursor-pointer'>
              <span className='text-xs font-medium text-gray-700 mb-1'>High</span>
              <input
                  type='color'
                  value={endColor}
                  onChange={(e) => setEndColor(e.target.value)}
                  className='w-8 h-8 rounded border border-gray-300 transition focus:outline-none focus:ring-1 focus:ring-indigo-500'
                  title='Pick High color'
              />
            </label>

            { /* Middle (center) */ }
            { useMiddleColor ? (
              <label className='flex flex-col items-center cursor-pointer'>
                <span className='text-xs font-medium text-gray-700 mb-1'>Medium</span>
                <input
                    type='color'
                    value={middleColor}
                    onChange={(e) => setMiddleColor(e.target.value)}
                    className='w-8 h-8 rounded border border-gray-300 transition focus:outline-none focus:ring-1 focus:ring-indigo-500'
                    title='Pick Medium color'
                />
              </label>
            ) : (
            // To keep spacing consistent when middle color not used,
            // add an invisible placeholder div with the same height.
              <div className='w-8 h-8' />
            ) }

            { /* Low (bottom) */ }
            <label className='flex flex-col items-center cursor-pointer'>
              <span className='text-xs font-medium text-gray-700 mb-1'>Low</span>
              <input
                  type='color'
                  value={startColor}
                  onChange={(e) => setStartColor(e.target.value)}
                  className='w-8 h-8 rounded border border-gray-300 transition focus:outline-none focus:ring-1 focus:ring-indigo-500'
                  title='Pick Low color'
              />
            </label>
          </div>
        </div>

        { /* Checkbox */ }
        <div className='flex items-center space-x-2 text-xs text-gray-700'>
          <input
              type='checkbox'
              id='use-middle'
              checked={useMiddleColor}
              onChange={(e) => setUseMiddleColor(e.target.checked)}
              className='w-3 h-3 text-indigo-600 border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 cursor-pointer'
          />
          <label htmlFor='use-middle' className='cursor-pointer select-none'>
            Add medium color
          </label>
        </div>
      </div>
    );
  },
);

export default ColorRangeSelector;
